import React from 'react';
import { PublicLayout } from '../../components/PublicLayout';
import { PageHero, BenefitsGrid, HowItWorks, CTASection } from '../../components/PageTemplate';
import { 
  Search, Filter, MapPin, Camera, Video, Calendar, 
  Send, Clock, Star 
} from 'lucide-react';

export default function TenantsFindApply() {
  const searchSteps = [
    {
      title: 'Set Your Criteria',
      description: 'Location, budget, bedrooms, move-in date. Save your search to get alerts.'
    },
    {
      title: 'Browse Listings',
      description: 'View photos, videos, floor plans, and 3D tours. All properties are verified.'
    },
    {
      title: 'Request Viewings',
      description: 'Book in-person viewings or request a video call tour - your choice.'
    },
    {
      title: 'Apply Instantly',
      description: 'Found the one? Apply with your saved profile in one click.'
    },
    {
      title: 'Track Your Application',
      description: 'See exactly where you are: submitted, under review, referencing, offer.'
    },
    {
      title: 'Accept & Pay',
      description: 'Offer accepted? Sign the contract and pay your deposit online.'
    }
  ];

  const features = [
    {
      icon: Search,
      title: 'Smart Search',
      description: 'Filter by price, location, property type, move-in date, and amenities.'
    },
    {
      icon: MapPin,
      title: 'Location-Based',
      description: 'Search by area, commute time, or distance from a specific point.'
    },
    {
      icon: Camera,
      title: 'Rich Media',
      description: 'Professional photos, video walkthroughs, and 3D virtual tours.'
    },
    {
      icon: Calendar,
      title: 'Easy Viewings',
      description: 'Book slots directly through the app. Get reminders before your viewing.'
    },
    {
      icon: Send,
      title: 'One-Click Apply',
      description: 'Your profile is saved. Apply to any property instantly.'
    },
    {
      icon: Clock,
      title: 'Application Tracking',
      description: 'Real-time updates on your application status. No chasing required.'
    }
  ];

  return (
    <PublicLayout>
      <PageHero
        badge="FIND & APPLY"
        title="Your Perfect Home"
        subtitle="Is Waiting"
        description="Browse verified properties, book viewings, and apply with one click. Your saved profile means no more repetitive forms."
        primaryCTA={{ href: '/properties', label: 'Start Searching' }}
        secondaryCTA={{ href: '/register', label: 'Create Profile' }}
        gradient="from-indigo-50 via-white to-slate-50"
      />

      <BenefitsGrid title="Search & Apply Features" benefits={features} />

      <HowItWorks title="From Search to Accepted" steps={searchSteps} />

      {/* Application Status Example */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-display font-bold text-center mb-8">
            Track Every Application
          </h2>
          <div className="space-y-4">
            {[
              { status: 'Submitted', date: 'Mon 15 Jan', complete: true },
              { status: 'Under Review', date: 'Tue 16 Jan', complete: true },
              { status: 'Referencing Started', date: 'Wed 17 Jan', complete: true },
              { status: 'Referencing Complete', date: 'Fri 19 Jan', complete: true },
              { status: 'Offer Made', date: 'Today', complete: false, current: true },
              { status: 'Contract Signed', complete: false },
              { status: 'Deposit Paid', complete: false },
            ].map((step, index) => (
              <div key={index} className={`flex items-center gap-4 p-4 rounded-xl ${
                step.current ? 'bg-primary/5 border-2 border-primary' : 
                step.complete ? 'bg-green-50' : 'bg-slate-50'
              }`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  step.complete ? 'bg-green-500 text-white' : 
                  step.current ? 'bg-primary text-white' : 'bg-slate-200'
                }`}>
                  {step.complete ? '✓' : index + 1}
                </div>
                <div className="flex-1">
                  <div className="font-medium">{step.status}</div>
                  {step.date && <div className="text-sm text-slate-500">{step.date}</div>}
                </div>
                {step.current && (
                  <span className="px-3 py-1 text-xs font-semibold text-primary bg-primary/10 rounded-full">
                    Current
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        title="Ready to start your search?"
        description="Browse available properties and apply with your saved profile."
        primaryCTA={{ href: '/properties', label: 'Browse Properties' }}
      />
    </PublicLayout>
  );
}
